import { createContext, useContext, useEffect, useState } from 'react';
import { useAxios } from 'src/hooks/use-axios';

export type FeatureFlags = {
  tsoSettingsPage: boolean;
  authMigration?: boolean;
  authMigrationForce?: boolean;
  authMigrationHardwareToken?: boolean;
  userInvitations?: boolean;
  immediateAssignments?: boolean;
  commodityFeeInTrades?: boolean;
};

const defaultFeatureFlags: FeatureFlags = {
  tsoSettingsPage: false,
  authMigration: false,
  authMigrationForce: false,
  authMigrationHardwareToken: false,
  // user invitiation is in general rolled out to production and not supposed to go away
  // again, so we default to true in case the monolotith is offline,
  // but we cannot completely remove this, because it is still supposed
  // to be false for KON
  userInvitations: true,
  immediateAssignments: false,
  commodityFeeInTrades: false,
};

const url = `${PRISMA_CONFIG.oldBackendUrl}/feature-toggles`;

const FeatureFlagsContext = createContext<FeatureFlags>(defaultFeatureFlags);

export function useFeatureFlagsRequest() {
  const [catched, setCatched] = useState(false);

  const featureFlags = useAxios(
    (axios, signal) =>
      axios
        .request<FeatureFlags>({
          signal,
          url,
          // if the old platform is too slow to respond (>= 10s) a timeout
          // error will be thrown - and we'll fallback to our default values
          timeout: 10000,
        })
        // Special error treatment!
        // This request is done very early in the life cycle of the app.
        // We can't really recover from it as there is nothing in place
        // to recover to. But if we return some usefull default here, the app at least
        // _starts_.
        .catch(() => setCatched(true)),
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    featureFlags.execute();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolved = Boolean(featureFlags.response) || catched;

  return [
    resolved,
    featureFlags.response ? featureFlags.response.data : defaultFeatureFlags,
  ] as const;
}

export function useFeatureFlags() {
  return useContext(FeatureFlagsContext);
}

export const FeatureFlagsProvider = FeatureFlagsContext.Provider;
